import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { tr } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Users, Mail, Phone, Pencil, Trash2, Check, ChevronRight, Clapperboard,
} from "lucide-react";
import ContactFormDialog from "@/components/crm/ContactFormDialog";
import ConfirmContactDialog from "@/components/crm/ConfirmContactDialog";
import AuditionsView from "@/components/crm/AuditionsView";
import {
  CRM_TYPES, bucketOf, statusOf, formatCountdown, formatDate, fullName,
} from "@/lib/crm";

const TABS = ["all", ...CRM_TYPES];

const DOT = { red: "bg-rose-500", yellow: "bg-amber-400", green: "bg-emerald-500" };

function StatusPill({ followup_at, t }) {
  const s = statusOf(followup_at);
  const label = s.key === "today" ? t("crm.status.today")
              : s.key === "soon"  ? t("crm.status.soon")
              : t("crm.status.active");
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      <span className={`inline-block w-2 h-2 rounded-full ${DOT[s.color]}`} />
      <span className="text-stone-300">{label}</span>
    </span>
  );
}

function ContactRow({ c, t, lang, onConfirm, onEdit, onDelete }) {
  const s = statusOf(c.followup_at);
  return (
    <li
      data-testid={`crm-row-${c.contact_id}`}
      className="grid grid-cols-12 gap-3 items-center px-4 py-4 border-t border-stone-900 hover:bg-stone-900/40 transition-colors"
    >
      <div className="col-span-3 min-w-0">
        <Link
          to={`/crm/${c.contact_id}`}
          data-testid={`crm-open-${c.contact_id}`}
          className="serif text-lg text-stone-100 hover:text-amber-400 truncate block"
        >
          {fullName(c)}
        </Link>
        {c.company && (
          <div className="mono text-[11px] text-stone-500 tracking-wider truncate">{c.company}</div>
        )}
      </div>
      <div className="col-span-2 mono text-[11px] uppercase tracking-widest text-stone-400 truncate">
        {t(`crm.type.${c.type}`)}
      </div>
      <div className="col-span-2 text-sm text-stone-300 truncate">{c.project_context || "—"}</div>
      <div className="col-span-2 text-xs text-stone-400 space-y-0.5 min-w-0">
        {c.email && (
          <a
            href={`mailto:${c.email}`}
            data-testid={`crm-mail-${c.contact_id}`}
            className="flex items-center gap-1 hover:text-amber-400 truncate"
          >
            <Mail size={11} /> <span className="truncate">{c.email}</span>
          </a>
        )}
        {c.phone && (
          <a
            href={`tel:${c.phone}`}
            data-testid={`crm-phone-${c.contact_id}`}
            className="flex items-center gap-1 hover:text-amber-400 truncate"
          >
            <Phone size={11} /> <span className="truncate">{c.phone}</span>
          </a>
        )}
        {!c.email && !c.phone && <span className="text-stone-600">—</span>}
      </div>
      <div className="col-span-1 text-xs text-stone-400">
        <div>{formatDate(c.last_contact_at, lang)}</div>
        <div className="text-stone-500 text-[10px] mono uppercase tracking-wider">
          → {formatDate(c.followup_at, lang)}
        </div>
      </div>
      <div className={`col-span-1 mono text-xs ${s.color === "red" ? "text-rose-400" : s.color === "yellow" ? "text-amber-400" : "text-emerald-400"}`}>
        {formatCountdown(c.followup_at, t)}
      </div>
      <div className="col-span-1"><StatusPill followup_at={c.followup_at} t={t} /></div>
      <div className="col-span-12 flex items-center justify-end gap-1 mt-2 xl:mt-0">
        <button
          data-testid={`crm-confirm-btn-${c.contact_id}`}
          onClick={() => onConfirm(c)}
          className="pill-btn !py-1.5 !px-3 text-xs"
          title={t("crm.action.confirm")}
        >
          <Check size={12} /> {t("crm.action.confirm")}
        </button>
        <button
          data-testid={`crm-edit-btn-${c.contact_id}`}
          onClick={() => onEdit(c)}
          className="ghost-btn !py-1.5 !px-2 text-xs"
        >
          <Pencil size={12} />
        </button>
        <button
          data-testid={`crm-delete-btn-${c.contact_id}`}
          onClick={() => onDelete(c)}
          className="ghost-btn !py-1.5 !px-2 text-xs hover:!text-rose-400 hover:!border-rose-500/50"
        >
          <Trash2 size={12} />
        </button>
        <Link
          to={`/crm/${c.contact_id}`}
          className="text-stone-500 hover:text-amber-400 ml-1"
          data-testid={`crm-chevron-${c.contact_id}`}
        >
          <ChevronRight size={14} />
        </Link>
      </div>
    </li>
  );
}

function DashboardSection({ title, dot, contacts, t, lang, onConfirm }) {
  return (
    <div className="paper p-5">
      <div className="flex items-center gap-2 mb-3">
        <span className={`inline-block w-2 h-2 rounded-full ${DOT[dot]}`} />
        <h3 className="mono text-[11px] tracking-[0.3em] text-stone-300 uppercase">{title}</h3>
        <span className="mono text-[10px] text-stone-500">· {contacts.length}</span>
      </div>
      {contacts.length === 0 ? (
        <div className="text-stone-500 text-xs italic">{t("crm.dash.empty")}</div>
      ) : (
        <ul className="space-y-2">
          {contacts.slice(0, 5).map((c) => (
            <li
              key={c.contact_id}
              className="flex items-center justify-between gap-2 text-sm py-1"
            >
              <Link
                to={`/crm/${c.contact_id}`}
                className="text-stone-200 hover:text-amber-400 truncate flex-1"
              >
                {fullName(c)}
                <span className="text-stone-500 mono text-[10px] ml-2 tracking-wider">
                  · {formatCountdown(c.followup_at, t)}
                </span>
              </Link>
              <button
                onClick={() => onConfirm(c)}
                data-testid={`crm-dash-confirm-${c.contact_id}`}
                className="ghost-btn !py-1 !px-2 text-[10px]"
              >
                <Check size={10} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* -------------------- Contacts View -------------------- */
function ContactsView({ t, lang }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmContact, setConfirmContact] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/crm/contacts");
      setContacts(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => (tab === "all" ? contacts : contacts.filter((c) => c.type === tab)),
    [contacts, tab],
  );

  const buckets = useMemo(() => {
    const today = [], upcoming = [], later = [];
    for (const c of contacts) {
      const b = bucketOf(c.followup_at);
      if (b === "today") today.push(c);
      else if (b === "upcoming") upcoming.push(c);
      else later.push(c);
    }
    const byFu = (a, b) => (new Date(a.followup_at || 0)) - (new Date(b.followup_at || 0));
    today.sort(byFu); upcoming.sort(byFu); later.sort(byFu);
    return { today, upcoming, later };
  }, [contacts]);

  const openNew = () => { setEditContact(null); setFormOpen(true); };
  const openEdit = (c) => { setEditContact(c); setFormOpen(true); };
  const openConfirm = (c) => { setConfirmContact(c); setConfirmOpen(true); };
  const delContact = async (c) => {
    if (!window.confirm(t("crm.confirm.delete"))) return;
    try {
      await api.delete(`/crm/contacts/${c.contact_id}`);
      setContacts((prev) => prev.filter((x) => x.contact_id !== c.contact_id));
      toast.success(t("crm.deleted"));
    } catch { toast.error(t("crm.delete_fail")); }
  };
  const onSaved = () => { load(); };
  const onConfirmed = () => { load(); };

  return (
    <>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div className="mono text-[11px] tracking-[0.3em] text-stone-500 uppercase">
          {t("crm.section.contacts")}
        </div>
        <button data-testid="crm-new-btn" onClick={openNew} className="pill-btn">
          <Plus size={16} /> {t("crm.action.new")}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <DashboardSection
          title={t("crm.dash.today")} dot="red" contacts={buckets.today}
          t={t} lang={lang} onConfirm={openConfirm}
        />
        <DashboardSection
          title={t("crm.dash.upcoming")} dot="yellow" contacts={buckets.upcoming}
          t={t} lang={lang} onConfirm={openConfirm}
        />
        <DashboardSection
          title={t("crm.dash.later")} dot="green" contacts={buckets.later}
          t={t} lang={lang} onConfirm={openConfirm}
        />
      </div>

      <div className="flex items-center gap-1 flex-wrap border-b border-stone-900 pb-2">
        {TABS.map((k) => (
          <button
            key={k}
            data-testid={`crm-tab-${k}`}
            onClick={() => setTab(k)}
            className={`ghost-btn !py-1.5 !px-3 text-xs ${
              tab === k ? "!border-amber-500/60 !text-amber-400" : ""
            }`}
          >
            {t(`crm.tab.${k}`)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mono text-stone-500 text-sm">{t("dash.loading")}</div>
      ) : filtered.length === 0 ? (
        <div className="paper p-10 text-center">
          <Users className="mx-auto text-stone-500" />
          <div className="serif text-2xl text-stone-200 mt-3">{t("crm.no_contacts")}</div>
          <button onClick={openNew} className="pill-btn mt-6" data-testid="crm-empty-new-btn">
            <Plus size={14} /> {t("crm.action.new")}
          </button>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="paper !p-0"
          data-testid="crm-table"
        >
          <div className="hidden xl:grid grid-cols-12 gap-3 px-4 py-3 mono text-[10px] uppercase tracking-widest text-stone-500 border-b border-stone-900">
            <div className="col-span-3">{t("crm.col.person")}</div>
            <div className="col-span-2">{t("crm.col.type")}</div>
            <div className="col-span-2">{t("crm.col.project")}</div>
            <div className="col-span-2">{t("crm.col.email_phone")}</div>
            <div className="col-span-1">{t("crm.col.last")} · {t("crm.col.followup")}</div>
            <div className="col-span-1">{t("crm.col.countdown")}</div>
            <div className="col-span-1">{t("crm.col.status")}</div>
          </div>
          <ul>
            {filtered.map((c) => (
              <ContactRow
                key={c.contact_id} c={c} t={t} lang={lang}
                onConfirm={openConfirm} onEdit={openEdit} onDelete={delContact}
              />
            ))}
          </ul>
        </motion.div>
      )}

      <ContactFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        contact={editContact}
        onSaved={onSaved}
      />
      <ConfirmContactDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        contact={confirmContact}
        onConfirmed={onConfirmed}
      />
    </>
  );
}

/* -------------------- Root CRM shell -------------------- */
export default function CRM() {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);
  const [params, setParams] = useSearchParams();

  const initial = params.get("view") === "auditions" ? "auditions" : "contacts";
  const [section, setSection] = useState(initial);

  useEffect(() => {
    const p = new URLSearchParams(params);
    if (section === "auditions") p.set("view", "auditions"); else p.delete("view");
    setParams(p, { replace: true });
    // eslint-disable-next-line
  }, [section]);

  const SectionTab = ({ id, label, icon: Icon, testid }) => (
    <button
      onClick={() => setSection(id)}
      data-testid={testid}
      className={`flex items-center gap-2 py-3 px-1 text-sm border-b-2 transition-colors -mb-px ${
        section === id
          ? "border-amber-500 text-amber-400"
          : "border-transparent text-stone-400 hover:text-stone-200"
      }`}
    >
      <Icon size={14} />
      <span className="tracking-wide">{label}</span>
    </button>
  );

  return (
    <div data-testid="crm-page" className="space-y-8">
      <div>
        <div className="mono text-[11px] tracking-[0.4em] text-amber-500 uppercase flex items-center gap-2">
          <Users size={12} /> {t("crm.page_title")}
        </div>
        <h1 className="serif text-5xl text-stone-100 mt-2">{t("crm.page_subtitle")}</h1>
        <p className="text-stone-400 mt-2 max-w-xl text-sm italic">{t("crm.page_tagline")}</p>
        <p className="text-stone-500 mt-3 max-w-xl text-xs italic" data-testid="crm-usage-note">
          {lang === "en"
            ? "CRM is for managing your professional contacts and relationships. Store only information needed for that purpose and use it in accordance with applicable law."
            : "CRM służy do zarządzania kontaktami i relacjami zawodowymi. Przechowuj wyłącznie informacje potrzebne do tego celu i korzystaj z nich zgodnie z obowiązującymi przepisami."}
        </p>
      </div>

      {/* Section switcher — Contacts | Auditions */}
      <div className="flex items-center gap-6 border-b border-stone-800">
        <SectionTab
          id="contacts"
          label={t("crm.section.contacts")}
          icon={Users}
          testid="crm-section-contacts"
        />
        <SectionTab
          id="auditions"
          label={t("crm.section.auditions")}
          icon={Clapperboard}
          testid="crm-section-auditions"
        />
      </div>

      {section === "contacts" ? (
        <ContactsView t={t} lang={lang} />
      ) : (
        <AuditionsView t={t} lang={lang} />
      )}
    </div>
  );
}
